import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import { FolderOpen, Search, FileText, ExternalLink, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "../lib/api";
import { TypeBadge } from "../components/Badges";

const Documents = () => {
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/documents", { params: { search: search || undefined } });
      setDocs(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="documents-page" className="space-y-5">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Document Library</h1>
        <p className="text-base text-slate-600 dark:text-slate-400">
          Every price list and brand document you've ever captured — search by filename, brand or event.
        </p>
      </div>

      <div className="relative" data-testid="library-search-wrap">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
        <input
          data-testid="library-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search PDFs — e.g. Archis, price list, bedsheet…"
          className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 pl-11 pr-3 py-3 text-base text-slate-900 dark:text-slate-100 placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none"
        />
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center" data-testid="library-loading">
          <div className="h-8 w-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
        </div>
      ) : docs.length === 0 ? (
        <div data-testid="library-empty" className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-10 text-center">
          <FolderOpen className="mx-auto h-8 w-8 text-slate-500 mb-2" />
          <p className="text-base text-slate-600 dark:text-slate-400">
            {search ? `No documents match "${search}".` : "No documents yet. Upload a price list on the Capture page."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-grid">
          {docs.map((d) => (
            <div
              key={d.id}
              data-testid={`doc-card-${d.id}`}
              className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden card-hover"
            >
              <div className="h-44 bg-slate-100 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                {d.preview_path ? (
                  <img
                    src={fileUrl(d.preview_path)}
                    alt={d.filename}
                    data-testid={`doc-preview-${d.id}`}
                    className="h-full w-full object-cover object-top"
                  />
                ) : (
                  <FileText className="h-10 w-10 text-slate-500" />
                )}
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  {d.event_type && <TypeBadge type={d.event_type} />}
                  <span className="ml-auto text-sm font-mono text-slate-600 dark:text-slate-400" data-testid={`doc-age-${d.id}`}>
                    {d.created_at ? formatDistanceToNow(new Date(d.created_at), { addSuffix: true }) : ""}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 leading-snug break-all">{d.filename}</h3>
                {d.vendor_name && (
                  <p className="text-sm font-mono uppercase tracking-wider text-slate-600 dark:text-slate-400">{d.vendor_name}</p>
                )}
                {d.event_title && <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{d.event_title}</p>}
                <div className="flex items-center gap-2 pt-1">
                  <a
                    data-testid={`doc-open-${d.id}`}
                    href={fileUrl(d.storage_path)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-1.5 text-sm font-bold text-emerald-950 hover:bg-emerald-400 transition-colors"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> Open
                  </a>
                  {d.event_id && (
                    <button
                      data-testid={`doc-event-link-${d.id}`}
                      onClick={() => navigate(`/events/${d.event_id}`)}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      Event <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Documents;
